import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import Swal from 'sweetalert2';
import { TarefasEmpresaService } from 'src/app/empresas/services/tarefas-empresa.service';
import { EmpresaBuscaService } from 'src/app/empresas/services/empresa-busca.service';
import { HomeService } from 'src/app/home/home-service/home.service';

export interface ContatoTarefa {
  id: number;
  name: string;
}

export interface EmpresaAutocomplete {
  id: number;
  name: string;
  tradeName: string;
  city: string;
  state: string;
  totalWorks: number;
}

@Component({
  selector: 'app-criar-tarefa-empresa',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './criar-tarefa-empresa.component.html',
  styleUrl: './criar-tarefa-empresa.component.scss',
})
export class CriarTarefaEmpresaComponent implements OnInit {
  @Input() empresaId = 0;
  @Input() empresaNome = '';
  @Input() contatos: ContatoTarefa[] = [];
  @Input() selecionarEmpresa = false;

  @Output() fechar = new EventEmitter<void>();
  @Output() tarefaCriada = new EventEmitter<void>();

  responsibleID = '';
  actionID = '';
  data = '';
  hora = '';
  description = '';
  erro = false;
  carregando = false;

  responsaveis: any[] = [];
  acoes: any[] = [];

  termoEmpresa = '';
  empresasEncontradas: EmpresaAutocomplete[] = [];
  buscandoEmpresas = false;
  carregandoContatos = false;
  contatosSelecionados: number[] = [];
  termoContato = '';

  private readonly _buscaEmpresa$ = new Subject<string>();

  private readonly _tarefasService = inject(TarefasEmpresaService);
  private readonly _empresaBuscaService = inject(EmpresaBuscaService);
  private readonly _homeService = inject(HomeService);

  ngOnInit(): void {
    this.popularResponsaveis();
    this.popularAcoes();

    this._buscaEmpresa$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((termo) => this.buscarEmpresas(termo));
  }

  popularResponsaveis(): void {
    this._homeService.listResponsables().subscribe({
      next: (res: any) => {
        this.responsaveis = (res?.data ?? []).sort((a: any, b: any) => a.name.localeCompare(b.name));
      },
      error: () => {},
    });
  }

  popularAcoes(): void {
    this._tarefasService.listarAcoes().subscribe({
      next: (res: any) => {
        this.acoes = res?.data ?? [];
      },
      error: () => {},
    });
  }

  aoDigitarEmpresa(termo: string): void {
    this.empresaId = 0;
    this.contatos = [];
    this.contatosSelecionados = [];

    if (!termo || termo.trim().length < 3) {
      this.empresasEncontradas = [];
      return;
    }

    this._buscaEmpresa$.next(termo.trim());
  }

  buscarEmpresas(termo: string): void {
    this.buscandoEmpresas = true;

    this._empresaBuscaService
      .listCompanyByName({
        name: termo,
        pagination: { page: 1, pageSize: 20, shortcuts: 10 },
      })
      .subscribe({
        next: (res: any) => {
          this.empresasEncontradas = res?.data?.records ?? [];
          this.buscandoEmpresas = false;
        },
        error: () => {
          this.empresasEncontradas = [];
          this.buscandoEmpresas = false;
        },
      });
  }

  selecionarEmpresaBusca(empresa: EmpresaAutocomplete): void {
    this.empresaId = empresa.id;
    this.empresaNome = empresa.tradeName || empresa.name;
    this.termoEmpresa = this.empresaNome;
    this.empresasEncontradas = [];
    this.contatos = [];
    this.contatosSelecionados = [];

    this.carregarContatos();
  }

  carregarContatos(): void {
    this.carregandoContatos = true;

    this._empresaBuscaService
      .getCompanyContacts({
        targetCompanyID: this.empresaId,
        pagination: { page: 1, pageSize: 200, shortcuts: 10 },
      })
      .subscribe({
        next: (res: any) => {
          this.contatos = (res?.data?.records ?? [])
            .filter((c: any) => !!c.contactId)
            .map((c: any) => ({ id: c.contactId, name: c.name }))
            .sort((a: ContatoTarefa, b: ContatoTarefa) =>
              (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' }),
            );
          this.carregandoContatos = false;
        },
        error: () => {
          this.contatos = [];
          this.carregandoContatos = false;
        },
      });
  }

  get contatosFiltrados(): ContatoTarefa[] {
    const termo = this.normalizar(this.termoContato);
    if (!termo) return this.contatos;

    return this.contatos.filter((c) => this.normalizar(c.name).includes(termo));
  }

  private normalizar(texto: string): string {
    return (texto ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  alternarContato(id: number): void {
    const indice = this.contatosSelecionados.indexOf(id);

    if (indice >= 0) {
      this.contatosSelecionados.splice(indice, 1);
    } else {
      this.contatosSelecionados.push(id);
    }
  }

  get totalContatosSelecionados(): number {
    return this.obterContactIds().length;
  }

  /** Data mínima (hoje) no formato aceito pelo input[type=date] */
  get dataMinima(): string {
    const hoje = new Date();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `${hoje.getFullYear()}-${mes}-${dia}`;
  }

  /** Hora mínima somente quando a data escolhida é hoje */
  get horaMinima(): string | null {
    if (this.data !== this.dataMinima) return null;

    const agora = new Date();
    const horas = String(agora.getHours()).padStart(2, '0');
    const minutos = String(agora.getMinutes()).padStart(2, '0');
    return `${horas}:${minutos}`;
  }

  aoAlterarData(): void {
    const minima = this.horaMinima;
    if (minima && this.hora && this.hora < minima) {
      this.hora = '';
    }
  }

  private dataHoraEhPassada(): boolean {
    if (!this.data || !this.hora) return false;
    return new Date(`${this.data}T${this.hora}`) < new Date();
  }

  private obterContactIds(): number[] {
    return this.selecionarEmpresa
      ? this.contatosSelecionados
      : this.contatos.map((c) => c.id).filter((id) => !!id);
  }

  formatFollowUp(): string {
    if (!this.data || !this.hora) return '';
    const [yyyy, mm, dd] = this.data.split('-');
    const horaFormatada = this.hora.length === 5 ? `${this.hora}:00` : this.hora;
    return `${yyyy}-${mm}-${dd}T${horaFormatada}`;
  }

  criar(): void {
    this.erro = false;

    const contactIds = this.obterContactIds();

    if (
      !this.empresaId ||
      !contactIds.length ||
      !this.responsibleID ||
      !this.actionID ||
      !this.data ||
      !this.hora
    ) {
      this.erro = true;
      return;
    }

    if (this.dataHoraEhPassada()) {
      this.erro = true;
      Swal.fire('Atenção', 'Selecione uma data e hora futuras.', 'warning');
      return;
    }

    const body = {
      targetCompanyID: this.empresaId,
      contactIds,
      responsibleID: Number(this.responsibleID),
      actionID: Number(this.actionID),
      followUp: this.formatFollowUp(),
      description: this.description,
    };

    this.carregando = true;
    this._tarefasService.criarTarefa(body).subscribe({
      next: (res: any) => {
        this.carregando = false;
        const criadas = res?.data?.created ?? contactIds.length;
        const mensagem =
          criadas > 1 ? `${criadas} tarefas criadas com sucesso!` : 'Tarefa criada com sucesso!';

        Swal.fire('Sucesso!', mensagem, 'success').then(() => {
          this.tarefaCriada.emit();
          this.fechar.emit();
        });
      },
      error: (err) => {
        this.carregando = false;
        Swal.fire('Erro', err?.error?.message ?? 'Não foi possível criar a tarefa.', 'error');
      },
    });
  }
}